// Riconoscimento automatico P.IVA (11 cifre numeriche) vs Codice Fiscale (16 alfanumerico)
export function detectTipoIdentificativo(valore) {
  if (!valore) return null;
  const clean = valore.trim().toUpperCase().replace(/\s/g, "");

  // P.IVA italiana: 11 cifre numeriche
  if (/^\d{11}$/.test(clean)) {
    return { tipo: "azienda", valore: clean, valido: true };
  }

  // Codice Fiscale italiano: 16 caratteri alfanumerici (formato standard persona fisica)
  if (/^[A-Z0-9]{16}$/.test(clean) && /[A-Z]/.test(clean) && /\d/.test(clean)) {
    return { tipo: "privato", valore: clean, valido: true };
  }

  // Valore presente ma non riconosciuto come formato valido
  return { tipo: null, valore: clean, valido: false };
}

// Validazione rapida durante la digitazione (non blocca, solo segnala)
export function validateIdentificativo(valore) {
  if (!valore || !valore.trim()) return { valid: false, message: "" };
  const result = detectTipoIdentificativo(valore);
  if (!result.valido) {
    return { valid: false, message: "Inserisci una P.IVA (11 cifre) o un Codice Fiscale (16 caratteri) validi" };
  }
  if (result.tipo === "azienda") {
    return { valid: true, message: "✓ Partita IVA riconosciuta", tipo: "azienda" };
  }
  return { valid: true, message: "✓ Codice Fiscale riconosciuto", tipo: "privato" };
}

// Normalizza il nome cliente per matching/ricerca (case-insensitive, spazi puliti)
export function normalizeNome(nome) {
  return (nome || "").trim().toLowerCase().replace(/\s+/g, " ");
}

// ─── Find-or-create cliente (deduplicazione automatica) ───────────────
// Usata in fase di creazione commessa: cerca cliente esistente per P.IVA/CF,
// se non esiste lo crea. Non duplica mai.
import { collection, query, where, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

export async function findOrCreateCliente({ nome, identificativo, email, telefono, organizationId }) {
  const detected = detectTipoIdentificativo(identificativo);
  if (!detected.valido) {
    throw new Error("P.IVA o Codice Fiscale non valido");
  }

  const fieldName = detected.tipo === "azienda" ? "piva" : "codiceFiscale";

  // STEP 1: cerca cliente esistente per identificativo univoco (univoco per org)
  const q = query(
    collection(db, "clienti"),
    where("organizationId", "==", organizationId),
    where(fieldName, "==", detected.valore)
  );
  const snap = await getDocs(q);

  if (!snap.empty) {
    // STEP 3: cliente esiste già — riusa
    const existing = snap.docs[0];
    return { id: existing.id, ...existing.data(), _wasExisting: true };
  }

  // STEP 2: non esiste — crealo
  const data = {
    nome: nome.trim(),
    tipoCliente: detected.tipo,
    piva: detected.tipo === "azienda" ? detected.valore : null,
    codiceFiscale: detected.tipo === "privato" ? detected.valore : null,
    email: email?.trim() || null,
    telefono: telefono?.trim() || null,
    organizationId,
    // Campi estesi per chi poi usa la sezione Clienti Pro
    ragioneSociale: nome.trim(),
    referente: "",
    stato: "Attivo",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, "clienti"), data);
  return { id: ref.id, ...data, _wasExisting: false };
}

// ─── Cliente Health Score ──────────────────────────────────────────────
// Calcola un punteggio 0-100 basato su: numero commesse, valore economico,
// ultima attività, tempo medio tra commesse, % completamento, lavori aperti.
export function calcClienteHealthScore(commesse) {
  if (!commesse || commesse.length === 0) {
    return { score: 0, label: "Nessuna commessa", color: "#9ca3af", emoji: "⚪" };
  }

  const now = new Date();
  const nCommesse = commesse.length;
  const valoreTotale = commesse.reduce((s,c) => s + (parseFloat(c.valoreEconomico)||0), 0);
  const attive = commesse.filter(c => !c.archived);
  const completate = commesse.filter(c => c.archived); // archiviata = considerata conclusa

  // Ultima attività (data creazione più recente tra le commesse)
  const dates = commesse.map(c => c.createdAt?.toMillis?.() || 0).filter(Boolean);
  const ultimaAttivitaMs = dates.length ? Math.max(...dates) : 0;
  const mesiDaUltimaAttivita = ultimaAttivitaMs ? (now.getTime() - ultimaAttivitaMs) / (1000*60*60*24*30) : 999;

  // Tempo medio tra commesse (in mesi)
  let tempoMedioMesi = 0;
  if (dates.length > 1) {
    const sorted = [...dates].sort((a,b)=>a-b);
    const intervalli = [];
    for (let i=1; i<sorted.length; i++) intervalli.push(sorted[i]-sorted[i-1]);
    const mediaMs = intervalli.reduce((a,b)=>a+b,0) / intervalli.length;
    tempoMedioMesi = mediaMs / (1000*60*60*24*30);
  }

  // Percentuale completamento (commesse archiviate = concluse correttamente)
  const pctCompletamento = nCommesse > 0 ? (completate.length / nCommesse) * 100 : 0;

  // ─── Calcolo punteggio (pesi totali = 100) ─────────────────────────
  let score = 0;

  // 1. Numero commesse (max 20 punti) — più commesse, più storico
  score += Math.min(20, nCommesse * 3);

  // 2. Valore economico (max 20 punti) — scala logaritmica per non premiare solo i grandi numeri
  if (valoreTotale > 0) score += Math.min(20, Math.log10(valoreTotale + 1) * 4);

  // 3. Ultima attività (max 25 punti) — penalizza inattività prolungata
  if (mesiDaUltimaAttivita <= 1) score += 25;
  else if (mesiDaUltimaAttivita <= 3) score += 18;
  else if (mesiDaUltimaAttivita <= 6) score += 10;
  else if (mesiDaUltimaAttivita <= 12) score += 4;
  else score += 0;

  // 4. Tempo medio tra commesse (max 15 punti) — più frequente = meglio
  if (tempoMedioMesi > 0) {
    if (tempoMedioMesi <= 3) score += 15;
    else if (tempoMedioMesi <= 6) score += 10;
    else if (tempoMedioMesi <= 12) score += 5;
    else score += 2;
  } else if (nCommesse === 1) {
    score += 7; // cliente con una sola commessa: punteggio neutro su questo fattore
  }

  // 5. Percentuale completamento (max 10 punti)
  score += (pctCompletamento / 100) * 10;

  // 6. Lavori aperti (max 10 punti) — avere commesse attive è positivo (cliente operativo)
  score += Math.min(10, attive.length * 2.5);

  score = Math.max(0, Math.min(100, Math.round(score)));

  // ─── Etichetta e colore ─────────────────────────────────────────────
  let label, color, emoji;
  if (mesiDaUltimaAttivita >= 12) {
    label = `Cliente inattivo da ${Math.round(mesiDaUltimaAttivita)} mesi`;
    color = "#dc2626"; emoji = "🔴";
  } else if (score >= 80) {
    label = "Cliente eccellente"; color = "#059669"; emoji = "🟢";
  } else if (score >= 55) {
    label = "Cliente buono"; color = "#2563eb"; emoji = "🟢";
  } else if (score >= 35) {
    label = "Sta diminuendo l'attività"; color = "#d97706"; emoji = "🟠";
  } else {
    label = "Richiede attenzione"; color = "#dc2626"; emoji = "🔴";
  }

  return {
    score, label, color, emoji,
    dettagli: { nCommesse, valoreTotale, mesiDaUltimaAttivita: Math.round(mesiDaUltimaAttivita), tempoMedioMesi: Math.round(tempoMedioMesi), pctCompletamento: Math.round(pctCompletamento), attive: attive.length }
  };
}

// ─── Indice fedeltà cliente (anni come cliente + n. commesse + valore) ──
export function calcIndiceFedelta(commesse) {
  if (!commesse || commesse.length === 0) return { stelle: 0, anni: 0, label: "Cliente nuovo" };
  const dates = commesse.map(c => c.createdAt?.toMillis?.()).filter(Boolean);
  if (dates.length === 0) return { stelle: 1, anni: 0, label: "Cliente nuovo" };
  const primaData = Math.min(...dates);
  const anni = (Date.now() - primaData) / (1000*60*60*24*365);
  const valoreTotale = commesse.reduce((s,c) => s + (parseFloat(c.valoreEconomico)||0), 0);

  let stelle = 1;
  if (anni >= 5 && commesse.length >= 20) stelle = 5;
  else if (anni >= 3 && commesse.length >= 10) stelle = 4;
  else if (anni >= 1.5 && commesse.length >= 5) stelle = 3;
  else if (anni >= 0.5 || commesse.length >= 2) stelle = 2;

  const label = stelle >= 4 ? "Cliente storico" : stelle >= 3 ? "Cliente consolidato" : stelle >= 2 ? "Cliente in crescita" : "Cliente nuovo";

  return { stelle, anni: Math.round(anni*10)/10, label, nCommesse: commesse.length, valoreTotale };
}

// ─── Parsing valuta in formato italiano ────────────────────────────────
// L'utente scrive nel formato che conosce: punto = separatore migliaia,
// virgola = separatore decimali (es. "45.000,50" oppure semplicemente "45000").
// <input type="number"> nativo HTML usa il formato americano inverso e
// interpreta "45.000" come 45 — causa di errori gravi su importi economici.
export function parseValutaItaliana(valore) {
  if (valore === null || valore === undefined || valore === "") return 0;
  if (typeof valore === "number") return valore;
  let clean = String(valore).trim();
  // Rimuove tutto tranne cifre, punti, virgole e segno meno
  clean = clean.replace(/[^\d.,-]/g, "");
  if (!clean) return 0;

  const hasComma = clean.includes(",");
  const hasDot = clean.includes(".");

  if (hasComma && hasDot) {
    // Formato italiano completo: 45.000,50 → punto=migliaia, virgola=decimali
    clean = clean.replace(/\./g, "").replace(",", ".");
  } else if (hasComma && !hasDot) {
    // Solo virgola: è il separatore decimale (es. 45,50)
    clean = clean.replace(",", ".");
  } else if (hasDot && !hasComma) {
    // Solo punto/i: trattalo come separatore delle migliaia se ci sono 3 cifre dopo l'ultimo punto
    // e più di un gruppo, altrimenti come decimale (es. "45.5" = 45.5, "45.000" = 45000)
    const parts = clean.split(".");
    const lastPart = parts[parts.length - 1];
    if (parts.length > 1 && lastPart.length === 3 && parts.length >= 2) {
      // Tutti i punti tranne eventualmente l'ultimo gruppo da 3 cifre = separatori migliaia
      clean = parts.join("");
    }
    // altrimenti lascia il punto come decimale (es. "45.5")
  }

  const num = parseFloat(clean);
  return isNaN(num) ? 0 : num;
}

// Formatta un numero nel formato italiano per la visualizzazione (es. 45000 → "45.000")
export function formatValutaItaliana(numero) {
  const n = parseFloat(numero) || 0;
  return n.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ─── AI Business Insight ────────────────────────────────────────────────
// Genera un messaggio dinamico basato sui dati reali del cliente.
// Confronta gli ultimi 12 mesi con i 12 precedenti per rilevare trend.
export function generateBusinessInsight(cliente, commesse) {
  if (!commesse || commesse.length === 0) {
    return { livello: "neutro", emoji: "⚪", testo: `${cliente.ragioneSociale||cliente.nome} non ha ancora commesse registrate. Aggiungi la prima commessa per iniziare a costruire lo storico.` };
  }

  const now = Date.now();
  const giorno = 1000*60*60*24;
  const dodiciMesiFa = now - 365*giorno;
  const ventiquattroMesiFa = now - 730*giorno;

  const dates = commesse.map(c => c.createdAt?.toMillis?.() || 0).filter(Boolean);
  const ultimaAttivitaMs = dates.length ? Math.max(...dates) : 0;
  const mesiDaUltimaAttivita = ultimaAttivitaMs ? Math.round((now - ultimaAttivitaMs) / (giorno*30)) : 999;

  const commesseUltimi12 = commesse.filter(c => (c.createdAt?.toMillis?.()||0) >= dodiciMesiFa);
  const commessePrecedenti12 = commesse.filter(c => {
    const t = c.createdAt?.toMillis?.()||0;
    return t >= ventiquattroMesiFa && t < dodiciMesiFa;
  });

  const valoreUltimi12 = commesseUltimi12.reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);
  const valorePrecedenti12 = commessePrecedenti12.reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);

  let trendPct = null;
  if (valorePrecedenti12 > 0) {
    trendPct = Math.round(((valoreUltimi12 - valorePrecedenti12) / valorePrecedenti12) * 100);
  }

  const nome = cliente.ragioneSociale || cliente.nome || "Questo cliente";

  // ─── Cliente inattivo da molto tempo: priorità massima, allarme rosso ──
  if (mesiDaUltimaAttivita >= 12) {
    return {
      livello: "rosso", emoji: "🔴",
      testo: `L'ultima attività con ${nome} risale a ${mesiDaUltimaAttivita} mesi fa. Cliente probabilmente inattivo — valuta se ricontattarlo o se ha cambiato fornitore.`
    };
  }

  // ─── Nessuna nuova commessa da diversi mesi: attenzione ────────────────
  if (mesiDaUltimaAttivita >= 6) {
    return {
      livello: "arancione", emoji: "🟠",
      testo: `Nessuna nuova commessa con ${nome} negli ultimi ${mesiDaUltimaAttivita} mesi. Potrebbe essere utile ricontattare il cliente per capire se ci sono nuove esigenze.`
    };
  }

  // ─── Crescita significativa: opportunità commerciale ───────────────────
  if (trendPct !== null && trendPct >= 15) {
    return {
      livello: "verde", emoji: "🟢",
      testo: `${nome} è uno dei tuoi clienti più importanti. Negli ultimi 12 mesi il valore delle commesse è aumentato del ${trendPct}%. Potrebbe essere il momento giusto per proporre nuovi servizi.`
    };
  }

  // ─── Calo significativo ma non ancora critico ──────────────────────────
  if (trendPct !== null && trendPct <= -25) {
    return {
      livello: "arancione", emoji: "🟠",
      testo: `Il valore delle commesse di ${nome} è diminuito del ${Math.abs(trendPct)}% rispetto ai 12 mesi precedenti. Vale la pena capire se ci sono criticità da risolvere.`
    };
  }

  // ─── Cliente nuovo con poche commesse: in osservazione ─────────────────
  if (commesse.length <= 2) {
    return {
      livello: "neutro", emoji: "⚪",
      testo: `${nome} è un cliente recente con ${commesse.length} commess${commesse.length===1?"a":"e"}. Continua a monitorare l'andamento per capire il potenziale di crescita.`
    };
  }

  // ─── Default: cliente stabile, attivo, nessun segnale particolare ──────
  const valTot = commesse.reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);
  return {
    livello: "verde", emoji: "🟢",
    testo: `${nome} è un cliente attivo con ${commesse.length} commesse gestite per un valore complessivo di € ${valTot.toLocaleString("it-IT",{maximumFractionDigits:0})}. Nessuna criticità rilevata.`
  };
}

// ─── Radar Cliente: semaforo + punti di forza/rischi/opportunità ───────
export function generateRadarCliente(cliente, commesse) {
  const health = calcClienteHealthScore(commesse);
  const insight = generateBusinessInsight(cliente, commesse);

  const puntiForza = [];
  const rischi = [];
  const opportunita = [];

  if (commesse.length === 0) {
    return { semaforo: "grigio", puntiForza, rischi: ["Nessuna commessa registrata"], opportunita: ["Proporre la prima commessa"] };
  }

  const now = Date.now();
  const giorno = 1000*60*60*24;
  const dates = commesse.map(c => c.createdAt?.toMillis?.() || 0).filter(Boolean);
  const ultimaAttivitaMs = dates.length ? Math.max(...dates) : 0;
  const mesiDaUltimaAttivita = ultimaAttivitaMs ? Math.round((now - ultimaAttivitaMs) / (giorno*30)) : 999;
  const valTot = commesse.reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);
  const attive = commesse.filter(c => !c.archived).length;

  // Punti di forza
  if (health.score >= 70) puntiForza.push("Health Score elevato");
  if (commesse.length >= 10) puntiForza.push(`Storico consolidato (${commesse.length} commesse)`);
  if (valTot >= 50000) puntiForza.push(`Valore economico significativo (€ ${valTot.toLocaleString("it-IT",{maximumFractionDigits:0})})`);
  if (mesiDaUltimaAttivita <= 1) puntiForza.push("Attività molto recente");
  if (attive >= 2) puntiForza.push(`${attive} commesse attualmente attive`);

  // Rischi
  if (mesiDaUltimaAttivita >= 12) rischi.push(`Inattivo da ${mesiDaUltimaAttivita} mesi`);
  else if (mesiDaUltimaAttivita >= 6) rischi.push(`Nessuna attività da ${mesiDaUltimaAttivita} mesi`);
  if (commesse.length === 1) rischi.push("Storico limitato a una sola commessa");
  if (attive === 0 && commesse.length > 0) rischi.push("Nessuna commessa attualmente attiva");

  // Opportunità
  if (mesiDaUltimaAttivita >= 6 && mesiDaUltimaAttivita < 12) opportunita.push("Ricontattare il cliente: potrebbe avere nuove esigenze");
  if (health.score >= 75 && commesse.length >= 3) opportunita.push("Cliente solido: valutare proposta di nuovi servizi");
  if (commesse.length >= 5 && valTot > 0) {
    const valoreMedio = valTot / commesse.length;
    if (valoreMedio > 10000) opportunita.push("Valore medio commessa elevato: potenziale per progetti più ampi");
  }
  if (opportunita.length === 0 && rischi.length === 0) opportunita.push("Cliente stabile: mantenere il livello di servizio attuale");

  let semaforo = "verde";
  if (insight.livello === "rosso") semaforo = "rosso";
  else if (insight.livello === "arancione") semaforo = "giallo";
  else if (insight.livello === "neutro") semaforo = "giallo";

  return { semaforo, puntiForza, rischi, opportunita, health, insight };
}

// ─── Customer DNA: carta d'identità sintetica del cliente ──────────────
export function generateCustomerDNA(cliente, commesse) {
  const health = calcClienteHealthScore(commesse);
  const fedelta = calcIndiceFedelta(commesse);

  const valTot = commesse.reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);
  const dates = commesse.map(c => c.createdAt?.toMillis?.()).filter(Boolean);
  const annoInizio = dates.length ? new Date(Math.min(...dates)).getFullYear() : new Date().getFullYear();

  // Indice crescita: confronto ultimi 12 mesi vs precedenti 12
  const now = Date.now();
  const giorno = 1000*60*60*24;
  const dodiciMesiFa = now - 365*giorno;
  const ventiquattroMesiFa = now - 730*giorno;
  const valUltimi12 = commesse.filter(c=>(c.createdAt?.toMillis?.()||0)>=dodiciMesiFa).reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);
  const valPrecedenti12 = commesse.filter(c=>{const t=c.createdAt?.toMillis?.()||0; return t>=ventiquattroMesiFa&&t<dodiciMesiFa;}).reduce((s,c)=>s+(parseFloat(c.valoreEconomico)||0),0);
  const indiceCrescita = valPrecedenti12 > 0 ? Math.round(((valUltimi12-valPrecedenti12)/valPrecedenti12)*100) : null;

  // Indice puntualità: % commesse completate (archiviate) sul totale, come proxy di affidabilità del rapporto
  const completate = commesse.filter(c=>c.archived).length;
  const indicePuntualita = commesse.length > 0 ? Math.round((completate/commesse.length)*100) : null;

  return {
    nome: cliente.ragioneSociale || cliente.nome,
    stelle: fedelta.stelle,
    anno: annoInizio,
    nCommesse: commesse.length,
    valoreTotale: valTot,
    indiceFedelta: Math.round((fedelta.stelle/5)*100),
    indiceCrescita,
    indicePuntualita,
    healthScore: health.score,
    healthColor: health.color,
    healthEmoji: health.emoji,
  };
}
